import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Edit, Trash2, Link, Users, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { z } from "zod";
import { userGroupValidators } from "@shared/validators/admin/user-groups-validators";

type UserGroup = z.infer<typeof userGroupValidators.userGroupSchema>;

interface UserGroupActionsProps {
    userGroup: UserGroup;
    onEdit?: (userGroup: UserGroup) => void;
    onManageConnections?: (userGroup: UserGroup) => void;
    onManageUsers?: (userGroup: UserGroup) => void;
    onDelete?: (userGroup: UserGroup) => void;
}

export function UserGroupActions({ 
    userGroup, 
    onEdit, 
    onManageConnections,
    onManageUsers,
    onDelete 
}: UserGroupActionsProps) {
    const isIdpGroup = userGroup.externalId != null;
    const isDefaultGroup = (userGroup as any).isDefault === true;

    const handleDelete = () => {
        if (!onDelete) return;
        onDelete(userGroup);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button aria-label="Open menu" variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                    <DotsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[220px]">
                {!isDefaultGroup && (
                    <DropdownMenuItem onClick={() => onEdit?.(userGroup)}>
                        {isIdpGroup ? (
                            <>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                            </>
                        ) : (
                            <>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                            </>
                        )}
                    </DropdownMenuItem>
                )}
                {!isDefaultGroup && (
                    <DropdownMenuItem onClick={() => onManageUsers?.(userGroup)}>
                        <Users className="mr-2 h-4 w-4" />
                        {isIdpGroup ? "View Users" : "Manage Users"}
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onManageConnections?.(userGroup)}>
                    <Link className="mr-2 h-4 w-4" />
                    Manage Connections
                </DropdownMenuItem>
                {!isIdpGroup && !isDefaultGroup && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
